/**
 * OSPROD-BOOK: customer-facing public booking page at /book/:slug.
 *
 * NO auth — the visitor is a client who got the link in a calendar
 * invite or email and just wants to pick a free slot. Layout is
 * three-step: load link → present grouped slots → submit form.
 * The slot picker shows two weeks at a time and lets the customer
 * page forward; we cap the request range server-side at 60 days
 * so a degenerate visitor can't ask for the next decade.
 */

import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Calendar as CalendarIcon, Clock, Check } from 'lucide-react'
import {
  publicBookingApi,
  type PublicBookingLink,
  type PublicSlot,
} from '../services/api'
import { mediaUrl } from '../services/config'

const PAGE_DAYS = 14

function startOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
}

function formatDay(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

export default function PublicBookingPage() {
  const { slug } = useParams<{ slug: string }>()
  const [link, setLink] = useState<PublicBookingLink | null>(null)
  const [linkError, setLinkError] = useState<string | null>(null)
  const [slots, setSlots] = useState<PublicSlot[]>([])
  const [loadingSlots, setLoadingSlots] = useState(true)
  const [pageStart, setPageStart] = useState<Date>(startOfDay(new Date()))
  const [picked, setPicked] = useState<PublicSlot | null>(null)
  const [form, setForm] = useState({ name: '', email: '', notes: '' })
  const [confirming, setConfirming] = useState(false)
  const [confirmed, setConfirmed] = useState<{ startsAt: string; endsAt: string } | null>(null)
  const [bookingError, setBookingError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) return
    publicBookingApi.get(slug)
      .then((res) => setLink(res.data))
      .catch((err) => setLinkError(err?.response?.data?.error || 'Booking link not found'))
  }, [slug])

  useEffect(() => {
    if (!slug || !link) return
    setLoadingSlots(true)
    const from = pageStart.toISOString()
    const to = addDays(pageStart, PAGE_DAYS).toISOString()
    publicBookingApi.slots(slug, from, to)
      .then((res) => setSlots(res.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false))
  }, [slug, link, pageStart])

  const days = useMemo(() => {
    return Array.from({ length: PAGE_DAYS }, (_, i) => addDays(pageStart, i))
  }, [pageStart])

  const slotsByDay = useMemo(() => {
    const m = new Map<string, PublicSlot[]>()
    for (const s of slots) {
      const d = startOfDay(new Date(s.startsAt))
      const k = d.toISOString()
      if (!m.has(k)) m.set(k, [])
      m.get(k)!.push(s)
    }
    return m
  }, [slots])

  const handleBook = async () => {
    if (!slug || !picked) return
    setConfirming(true)
    setBookingError(null)
    try {
      const res = await publicBookingApi.book(slug, {
        startsAt: picked.startsAt,
        customerName: form.name.trim(),
        customerEmail: form.email.trim(),
        notes: form.notes.trim() || null,
      })
      setConfirmed({ startsAt: res.data.startsAt, endsAt: res.data.endsAt })
    } catch (err: any) {
      setBookingError(err?.response?.data?.error || 'Could not book. Try a different slot.')
    } finally {
      setConfirming(false)
    }
  }

  // 1) Hard error — invalid / expired link.
  if (linkError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="text-foreground text-xl font-semibold mb-2">Link not available</div>
          <p className="text-muted-foreground text-sm">{linkError}</p>
        </div>
      </div>
    )
  }

  // 2) Loading link.
  if (!link) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground text-sm">Loading…</div>
      </div>
    )
  }

  // 3) Confirmation screen.
  if (confirmed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-green-500/15 text-green-500 flex items-center justify-center mx-auto mb-4">
            <Check size={28} />
          </div>
          <div className="text-foreground text-xl font-semibold mb-2">You're booked!</div>
          <p className="text-muted-foreground text-sm mb-1">
            {new Date(confirmed.startsAt).toLocaleString(undefined, { weekday: 'long', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
          <p className="text-muted-foreground text-sm">
            with {link.owner.firstName || link.owner.username}. We sent a calendar invite to {form.email}.
          </p>
        </div>
      </div>
    )
  }

  const ownerName = [link.owner.firstName, link.owner.lastName].filter(Boolean).join(' ') || link.owner.username

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="px-6 py-5 border-b border-border bg-card">
        <div className="max-w-3xl mx-auto flex items-start gap-4">
          {link.owner.avatarUrl ? (
            <img src={mediaUrl(link.owner.avatarUrl)} alt="" className="w-14 h-14 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xl font-semibold shrink-0">
              {ownerName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] text-muted-foreground">{ownerName}</div>
            <h1 className="text-xl font-semibold text-foreground">{link.title}</h1>
            <div className="flex items-center gap-3 text-[12px] text-muted-foreground mt-1">
              <span className="inline-flex items-center gap-1">
                <Clock size={12} />
                {link.durationMinutes} min
              </span>
              <span className="inline-flex items-center gap-1">
                <CalendarIcon size={12} />
                Web meeting
              </span>
            </div>
            {link.description && (
              <p className="text-[13px] text-foreground/80 mt-3 whitespace-pre-line">{link.description}</p>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 px-6 py-6">
        <div className="max-w-3xl mx-auto">
          {!picked && (
            <>
              {/* OSPROD-BOOK followup: tell the visitor what zone
                  they're seeing — slots are formatted in their
                  browser's local zone, but the configured link
                  lives in `link.timezone`, so we name BOTH. */}
              <div className="text-[12px] text-muted-foreground mb-3">
                Times shown in your local timezone
                {' '}<span className="text-foreground/80">({Intl.DateTimeFormat().resolvedOptions().timeZone})</span>.
                {' '}Originally configured in <span className="text-foreground/80">{link.timezone}</span>.
              </div>
              <div className="flex items-center justify-between mb-4">
                <div className="text-[13px] text-foreground font-medium">
                  {pageStart.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setPageStart(addDays(pageStart, -PAGE_DAYS))}
                    disabled={pageStart <= startOfDay(new Date())}
                    className="h-8 w-8 rounded-md text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition"
                  >
                    <ArrowLeft size={14} className="mx-auto" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPageStart(addDays(pageStart, PAGE_DAYS))}
                    className="h-8 w-8 rounded-md text-foreground hover:bg-muted transition"
                  >
                    <ArrowRight size={14} className="mx-auto" />
                  </button>
                </div>
              </div>

              {loadingSlots && <div className="text-muted-foreground text-sm py-8 text-center">Loading slots…</div>}

              {!loadingSlots && (
                <div className="space-y-4">
                  {days.map((d) => {
                    const dayKey = startOfDay(d).toISOString()
                    const daySlots = slotsByDay.get(dayKey) ?? []
                    const isToday = sameDay(d, new Date())
                    if (daySlots.length === 0) return null
                    return (
                      <div key={dayKey} className="rounded-xl border border-border bg-card p-4">
                        <div className={`text-[13px] font-medium mb-2 ${isToday ? 'text-primary' : 'text-foreground'}`}>
                          {formatDay(d)}{isToday && ' — today'}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {daySlots.map((s) => (
                            <button
                              key={s.startsAt}
                              type="button"
                              onClick={() => setPicked(s)}
                              className="px-3 py-1.5 rounded-md border border-border bg-background hover:bg-primary hover:text-primary-foreground hover:border-primary text-[12.5px] text-foreground transition"
                            >
                              {formatTime(s.startsAt)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  })}

                  {slots.length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-foreground font-medium mb-1">No free slots in this period</div>
                      <p className="text-muted-foreground text-sm">Try jumping forward two weeks.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {picked && (
            <div className="rounded-xl border border-border bg-card p-6 max-w-lg mx-auto">
              <button
                type="button"
                onClick={() => setPicked(null)}
                className="text-[12px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
              >
                <ArrowLeft size={12} /> back to slots
              </button>
              <h2 className="text-foreground font-semibold mb-1">Confirm your booking</h2>
              <p className="text-[12.5px] text-muted-foreground mb-4">
                {new Date(picked.startsAt).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                {' · '}
                {formatTime(picked.startsAt)} – {formatTime(picked.endsAt)}
              </p>

              {bookingError && (
                <div className="text-sm bg-destructive/10 text-destructive rounded px-3 py-2 mb-3">{bookingError}</div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="text-[11px] uppercase text-muted-foreground tracking-wide block mb-1">Your name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-muted text-foreground rounded px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase text-muted-foreground tracking-wide block mb-1">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-muted text-foreground rounded px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase text-muted-foreground tracking-wide block mb-1">
                    Anything we should know? (optional)
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    className="w-full bg-muted text-foreground rounded px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-primary resize-none"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={handleBook}
                disabled={confirming || !form.name.trim() || !form.email.trim()}
                className="mt-4 w-full px-4 py-2 rounded-md bg-primary text-primary-foreground text-[13px] font-medium hover:bg-primary/90 transition disabled:opacity-50"
              >
                {confirming ? 'Booking…' : 'Confirm booking'}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
